import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import dayjs from 'dayjs';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';

import AuthContext from '../../context/authContext';
import useTodo from '../../hooks/useTodo';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import { sortTags, tagChipColor } from '../../utils/todoTags';
import { AUTOPILOT_HINT, autopilotMarkedBy, autopilotStatus } from '../../utils/autopilot';

// Columns of the developer board. The board is a view on the developer tasks:
// one card per task, no card without a task, and cards are only ever created by
// creating a task. "Erledigt" is bound to the task's completion.
const COLUMNS = [
  { key: 'open', label: 'Offen', color: '#0288d1', hint: 'Neu eingegangen, noch nicht gesichtet' },
  {
    key: 'inProgress',
    label: 'In Arbeit',
    color: '#ef6c00',
    hint: 'Wird gerade umgesetzt — der Melder wird benachrichtigt',
  },
  {
    key: 'done',
    label: 'Erledigt',
    color: '#388e3c',
    hint: 'Aufgabe ist abgehakt — der Melder wird benachrichtigt',
  },
];

const COLUMN_BY_KEY = Object.fromEntries(COLUMNS.map(column => [column.key, column]));

// Retired columns that may still appear in old move histories.
const RETIRED_COLUMN_LABELS = { acknowledged: 'Reagiert' };

const PRIORITY_CONFIG = {
  highest: { label: 'Sehr hoch', color: 'error' },
  high: { label: 'Hoch', color: 'warning' },
  normal: { label: 'Normal', color: 'info' },
  low: { label: 'Niedrig', color: 'default' },
  lowest: { label: 'Sehr niedrig', color: 'default' },
};

const PRIORITY_RANK = { highest: 0, high: 1, normal: 2, low: 3, lowest: 4 };

const formatAge = value => {
  if (!value) return '';
  const minutes = dayjs().diff(dayjs(value), 'minute');
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} Min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} Std`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `vor ${days} T`;
  return dayjs(value).format('DD.MM.YYYY');
};

const formatDateTime = value => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '–');

// A completed task always counts as done, whatever its stored column says.
const columnOf = todo => {
  const stored =
    todo.boardStatus ||
    (todo.status === 'completed' || todo.status === 'cancelled'
      ? 'done'
      : todo.devBoard?.status || 'open');
  // A status of a retired column (or anything unknown) renders as open.
  return COLUMN_BY_KEY[stored] ? stored : 'open';
};

function DraggableCard({ todo, disabled, renderCard }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useDraggable({
    id: todo._id,
    disabled,
    data: { todo },
  });
  const dragHandle = disabled ? null : (
    <Tooltip title='Spalte per Drag & Drop ändern'>
      <Box
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        onClick={event => event.stopPropagation()}
        aria-label='Spalte per Drag & Drop ändern'
        sx={{
          touchAction: 'none',
          cursor: isDragging ? 'grabbing' : 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 32,
          minHeight: 32,
          mr: -0.75,
          my: -0.5,
          borderRadius: 1,
          color: 'text.disabled',
          '&:hover': { color: 'text.secondary', bgcolor: 'action.hover' },
        }}
      >
        <DragIndicatorIcon fontSize='small' />
      </Box>
    </Tooltip>
  );

  return (
    <div ref={setNodeRef} style={{ opacity: isDragging ? 0.25 : 1 }}>
      {renderCard(todo, isDragging, dragHandle)}
    </div>
  );
}

function BoardColumn({ column, count, dragActive, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.key, data: { status: column.key } });
  return (
    <Paper
      ref={setNodeRef}
      variant='outlined'
      sx={{
        flex: { xs: '0 0 auto', md: '1 1 0' },
        minWidth: { xs: 0, md: 300 },
        width: { xs: '100%', md: 'auto' },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        bgcolor: alpha(column.color, isOver ? 0.12 : 0.05),
        borderColor: isOver ? column.color : 'divider',
        boxShadow: isOver ? `inset 0 0 0 1px ${column.color}` : 'none',
        transition: 'background-color 0.15s, border-color 0.15s, box-shadow 0.15s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}>
        <Tooltip title={column.hint}>
          <Typography
            variant='subtitle2'
            sx={{ fontWeight: 700, color: column.color, letterSpacing: 0.3 }}
          >
            {column.label}
          </Typography>
        </Tooltip>
        <Chip
          size='small'
          label={count}
          sx={{ bgcolor: alpha(column.color, 0.15), color: column.color, fontWeight: 600 }}
        />
        {dragActive && isOver && (
          <Typography variant='caption' sx={{ ml: 'auto', color: column.color, fontWeight: 700 }}>
            Hier ablegen
          </Typography>
        )}
      </Box>
      <Stack spacing={1} sx={{ px: 1, pb: 1.5, flex: 1, minHeight: { xs: 56, md: 120 } }}>
        {children}
      </Stack>
    </Paper>
  );
}

/**
 * "Dev Issues": kanban board of the developer to-dos. Cards are the tasks
 * themselves — they arise only by creating a task (manually or out of the
 * feedback tool), can be moved between the columns here, and dropping one in
 * "Erledigt" ticks the task off. Moves into In Arbeit / Erledigt inform the
 * author of the underlying feedback (conversation + mail).
 */
export default function DevIssuesBoardView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const authCtx = useContext(AuthContext);
  const { getDevBoard, moveDevBoardCard, setAutoRun, updateTodo } = useTodo();

  const canRead = authCtx.checkRight('dev:todo:read');
  const canManageRight = authCtx.checkRight('dev:todo:manage');

  const [todos, setTodos] = useState([]);
  const [backendCanManage, setBackendCanManage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  // Read-only mode: enforced by the backend for anyone without
  // dev:todo:manage, and switchable by hand so the board can be shown without
  // moving a card by accident.
  const [readOnly, setReadOnly] = useState(false);
  const [searchText, setSearchText] = useState('');
  // Tag filter, any-of. Client-side like on the list board: the whole backlog is
  // loaded anyway, and a refetch per click would fight the drag & drop.
  const [tagFilter, setTagFilter] = useState([]);
  // Autopilot filter: only cards currently marked for automatic processing
  // (autoRun.enabled), so the marked queue can be looked at on its own.
  const [autoRunOnly, setAutoRunOnly] = useState(false);
  const searchRef = useRef(null);
  const searchTimer = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const [movePending, setMovePending] = useState(false);
  const [autoPending, setAutoPending] = useState(false);
  const [aiInstructionsPending, setAiInstructionsPending] = useState(false);
  // Uncontrolled: the briefing is typed in long-hand and re-rendering the whole
  // board on every keystroke is not worth it.
  const aiInstructionsRef = useRef(null);
  const [detailTodo, setDetailTodo] = useState(null);

  const canManage = (backendCanManage || canManageRight) && !readOnly;

  const load = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const result = await getDevBoard();
      setTodos(result?.todos || []);
      setBackendCanManage(Boolean(result?.canManage));
    } catch {
      // Error toast is handled inside useTodo.
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, [canRead, getDevBoard]);

  useEffect(() => {
    load();
  }, [load]);

  const applySearch = useCallback(() => {
    setSearchText(searchRef.current?.value?.trim().toLowerCase() || '');
  }, []);

  const handleSearchChange = useCallback(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(applySearch, 300);
  }, [applySearch]);

  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    []
  );

  const columns = useMemo(() => {
    const byColumn = Object.fromEntries(COLUMNS.map(column => [column.key, []]));
    todos
      .filter(todo => tagFilter.length === 0 || (todo.tags || []).some(t => tagFilter.includes(t)))
      .filter(todo => !autoRunOnly || !!todo.autoRun?.enabled)
      .filter(todo => {
        if (!searchText) return true;
        const haystack = [
          todo.title,
          todo.description,
          todo.aiReason,
          todo.feedbackViewKey,
          todo.sourceDevFeedbackId?.text,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(searchText);
      })
      .forEach(todo => {
        const column = columnOf(todo);
        if (byColumn[column]) byColumn[column].push(todo);
      });
    COLUMNS.forEach(column =>
      byColumn[column.key].sort((a, b) => {
        const p = (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9);
        if (p) return p;
        const at = a.meta?.createdAt ? new Date(a.meta.createdAt).getTime() : 0;
        const bt = b.meta?.createdAt ? new Date(b.meta.createdAt).getTime() : 0;
        return bt - at;
      })
    );
    return byColumn;
  }, [todos, searchText, tagFilter, autoRunOnly]);

  // Every tag in the backlog with its frequency — derived from the unfiltered
  // list so a chip never disappears just because it is active.
  const tagOptions = useMemo(() => {
    const counts = new Map();
    for (const todo of todos) {
      for (const tag of todo.tags || []) counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
    return sortTags([...counts.keys()]).map(tag => ({ tag, count: counts.get(tag) ?? 0 }));
  }, [todos]);

  const toggleTag = useCallback(tag => {
    setTagFilter(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]));
  }, []);

  // How many cards are marked for automatic processing.
  const autoRunCount = useMemo(() => todos.filter(t => !!t.autoRun?.enabled).length, [todos]);

  const clearFilters = useCallback(() => {
    setTagFilter([]);
    setAutoRunOnly(false);
  }, []);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  const moveTo = useCallback(
    async (todo, target) => {
      const previous = columnOf(todo);
      if (previous === target || movePending) return;
      // Optimistic: the card jumps immediately and returns on an error.
      setTodos(prev =>
        prev.map(entry => (entry._id === todo._id ? { ...entry, boardStatus: target } : entry))
      );
      setMovePending(true);
      try {
        const updated = await moveDevBoardCard(todo._id, target);
        if (updated?._id) {
          setTodos(prev => prev.map(entry => (entry._id === todo._id ? updated : entry)));
          setDetailTodo(current => (current?._id === todo._id ? updated : current));
        }
        toast.success(`Verschoben nach „${COLUMN_BY_KEY[target].label}“`);
      } catch {
        // Error toast is handled inside useTodo.
        setTodos(prev =>
          prev.map(entry => (entry._id === todo._id ? { ...entry, boardStatus: previous } : entry))
        );
      } finally {
        setMovePending(false);
      }
    },
    [moveDevBoardCard, movePending]
  );

  // Marking a card hands it to the scheduled AUTOTODO run: it implements the
  // task, opens a pull request and deploys to staging — it never merges. Once a
  // human has merged, a later run ticks the task off. Switching the toggle is
  // the approval behind both moves the run announces to the reporter.
  const toggleAutoRun = useCallback(
    async (todo, enabled) => {
      setAutoPending(true);
      try {
        const updated = await setAutoRun(todo._id, enabled);
        if (updated?._id) {
          setTodos(prev => prev.map(entry => (entry._id === todo._id ? updated : entry)));
          setDetailTodo(current => (current?._id === todo._id ? updated : current));
        }
        toast.success(
          enabled
            ? 'Aufgabe für die automatische Abarbeitung markiert'
            : 'Automatische Abarbeitung beendet'
        );
      } catch {
        // Error toast is handled inside useTodo.
      } finally {
        setAutoPending(false);
      }
    },
    [setAutoRun]
  );

  // The briefing the marked card hands to the run that later claims it. Saved
  // when the field loses focus and only when it actually changed, so opening
  // the dialog and closing it again writes nothing.
  const saveAiInstructions = useCallback(
    async todo => {
      const next = (aiInstructionsRef.current?.value || '').trim();
      if (next === (todo.aiInstructions || '')) return;
      setAiInstructionsPending(true);
      try {
        const updated = await updateTodo(todo._id, { aiInstructions: next });
        if (updated?._id) {
          setTodos(prev => prev.map(entry => (entry._id === todo._id ? updated : entry)));
          setDetailTodo(current => (current?._id === todo._id ? updated : current));
        }
        toast.success(next ? 'Hinweise für die KI gespeichert' : 'Hinweise für die KI entfernt');
      } catch {
        // Error toast is handled inside useTodo.
      } finally {
        setAiInstructionsPending(false);
      }
    },
    [updateTodo]
  );

  const handleDragStart = useCallback(({ active }) => setActiveId(String(active.id)), []);
  const handleDragCancel = useCallback(() => setActiveId(null), []);
  const handleDragEnd = useCallback(
    ({ active, over }) => {
      setActiveId(null);
      if (!over) return;
      const dragged = todos.find(todo => todo._id === active.id);
      const target = over.data.current?.status;
      if (!dragged || !target) return;
      moveTo(dragged, target);
    },
    [todos, moveTo]
  );

  const renderCard = useCallback((todo, isDragging = false, dragHandle = null, isOverlay = false) => {
    const column = COLUMN_BY_KEY[columnOf(todo)];
    const isDone = column.key === 'done';
    const priorityCfg = todo.priority ? PRIORITY_CONFIG[todo.priority] : null;
    const feedbackText = todo.sourceDevFeedbackId?.text;
    return (
      <Paper
        variant='outlined'
        onClick={isOverlay ? undefined : () => setDetailTodo(todo)}
        sx={{
          p: 1.25,
          borderRadius: 2,
          cursor: 'pointer',
          borderLeft: `4px solid ${column.color}`,
          opacity: isDone ? 0.7 : 1,
          boxShadow: isDragging ? 4 : 0,
          '&:hover': { boxShadow: 3 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Chip
            size='small'
            label={priorityCfg ? priorityCfg.label : 'Prio offen'}
            color={priorityCfg ? priorityCfg.color : 'warning'}
            variant={priorityCfg ? 'filled' : 'outlined'}
          />
          {todo.aiGenerated && (
            <Tooltip title='Von der KI aus einem Feedback abgeleitet'>
              <SmartToyOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
            </Tooltip>
          )}
          {autopilotStatus(todo.autoRun) && (
            <Tooltip title={autopilotStatus(todo.autoRun).hint}>
              <RocketLaunchOutlinedIcon sx={{ fontSize: 15, color: 'info.main' }} />
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant='caption' color='text.disabled' sx={{ whiteSpace: 'nowrap' }}>
            {formatAge(todo.meta?.createdAt)}
          </Typography>
          {dragHandle}
        </Box>
        <Typography
          variant='body2'
          sx={{
            mt: 0.5,
            fontWeight: 600,
            wordBreak: 'break-word',
            textDecoration: isDone ? 'line-through' : 'none',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {todo.title}
        </Typography>
        {feedbackText && (
          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 13, mt: '2px', color: 'text.disabled' }} />
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {feedbackText}
            </Typography>
          </Box>
        )}
        {(todo.tags || []).length > 0 && (
          <Stack direction='row' spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
            {sortTags(todo.tags).map(tag => (
              <Chip key={tag} size='small' label={tag} color={tagChipColor(tag)} variant='outlined' />
            ))}
          </Stack>
        )}
      </Paper>
    );
  }, []);

  if (!canRead) return <Navigate to='/' replace />;

  const activeTodo = activeId ? todos.find(todo => todo._id === activeId) || null : null;
  const showSkeletons = loading && !loadedOnce;
  const detailColumn = detailTodo ? COLUMN_BY_KEY[columnOf(detailTodo)] : null;
  const detailPriority = detailTodo?.priority ? PRIORITY_CONFIG[detailTodo.priority] : null;

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant='h5' sx={{ lineHeight: 1.3 }}>
          Dev Issues
        </Typography>
      </Box>

      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Kanban-Board der Entwickler-Aufgaben. Jede Karte ist genau eine Aufgabe: Karten entstehen
        ausschließlich durch Aufgaben (manuell oder aus dem Feedback-Tool) und haken die Aufgabe in
        „Erledigt“ automatisch ab. In Arbeit und Erledigt benachrichtigen den Melder im
        Verlauf und per Mail.
      </Typography>

      <Paper variant='outlined' sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            inputRef={searchRef}
            size='small'
            placeholder='Suchen…'
            onChange={handleSearchChange}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (searchTimer.current) clearTimeout(searchTimer.current);
                applySearch();
              }
            }}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.disabled' }} /> }}
            sx={{ width: { xs: '100%', sm: 260 } }}
          />
          {canManageRight ? (
            <FormControlLabel
              control={
                <Switch
                  size='small'
                  checked={readOnly}
                  onChange={event => setReadOnly(event.target.checked)}
                />
              }
              label={<Typography variant='body2'>Nur-Lese-Modus</Typography>}
            />
          ) : (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <LockIcon sx={{ fontSize: 15 }} />
              Nur-Lese-Modus
            </Typography>
          )}
          <Tooltip title='Aktualisieren'>
            <span style={{ marginLeft: isMobile ? 0 : 'auto' }}>
              <IconButton size='small' onClick={load} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {(tagOptions.length > 0 || autoRunCount > 0) && (
          <Stack direction='row' spacing={0.75} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}>
            <Chip
              size='small'
              label='Alle'
              color={tagFilter.length === 0 && !autoRunOnly ? 'primary' : 'default'}
              variant={tagFilter.length === 0 && !autoRunOnly ? 'filled' : 'outlined'}
              onClick={clearFilters}
            />
            {autoRunCount > 0 && (
              <Chip
                size='small'
                icon={<RocketLaunchOutlinedIcon />}
                label={`Autopilot (${autoRunCount})`}
                color={autoRunOnly ? 'primary' : 'info'}
                variant={autoRunOnly ? 'filled' : 'outlined'}
                onClick={() => setAutoRunOnly(prev => !prev)}
              />
            )}
            {tagOptions.map(({ tag, count }) => (
              <Chip
                key={tag}
                size='small'
                label={`${tag} (${count})`}
                color={tagFilter.includes(tag) ? 'primary' : tagChipColor(tag)}
                variant={tagFilter.includes(tag) ? 'filled' : 'outlined'}
                onClick={() => toggleTag(tag)}
              />
            ))}
          </Stack>
        )}
      </Paper>

      {showSkeletons ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1.5,
            overflowX: { md: 'auto' },
            pb: 1,
          }}
        >
          {COLUMNS.map(column => (
            <Skeleton
              key={column.key}
              variant='rounded'
              height={isMobile ? 140 : 320}
              sx={{ flex: { md: '1 1 0' }, minWidth: { md: 300 }, borderRadius: 2 }}
            />
          ))}
        </Box>
      ) : (
        <DndContext
          sensors={dndSensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 1.5,
              overflowX: { md: 'auto' },
              alignItems: 'stretch',
              pb: 1,
            }}
          >
            {COLUMNS.map(column => (
              <BoardColumn
                key={column.key}
                column={column}
                count={columns[column.key].length}
                dragActive={Boolean(activeTodo)}
              >
                {columns[column.key].length === 0 ? (
                  <Box sx={{ py: 3, textAlign: 'center' }}>
                    <TaskAltIcon sx={{ fontSize: 26, color: 'text.disabled' }} />
                    <Typography variant='caption' color='text.disabled' sx={{ display: 'block' }}>
                      Keine Aufgaben
                    </Typography>
                  </Box>
                ) : (
                  columns[column.key].map(todo => (
                    <DraggableCard
                      key={todo._id}
                      todo={todo}
                      disabled={!canManage || movePending}
                      renderCard={renderCard}
                    />
                  ))
                )}
              </BoardColumn>
            ))}
          </Box>
          <DragOverlay dropAnimation={null}>
            {activeTodo ? renderCard(activeTodo, true, null, true) : null}
          </DragOverlay>
        </DndContext>
      )}

      <Dialog
        open={Boolean(detailTodo)}
        onClose={() => setDetailTodo(null)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle sx={{ pr: 6, fontSize: '1.05rem', lineHeight: 1.3 }}>
          {detailTodo?.title}
        </DialogTitle>
        {detailTodo && (
          <DialogContent dividers>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size='small'
                  label={detailPriority ? detailPriority.label : 'Prio offen'}
                  color={detailPriority ? detailPriority.color : 'warning'}
                  variant={detailPriority ? 'filled' : 'outlined'}
                />
                <Chip
                  size='small'
                  label={detailColumn.label}
                  sx={{
                    bgcolor: alpha(detailColumn.color, 0.15),
                    color: detailColumn.color,
                    fontWeight: 600,
                  }}
                />
                <Typography variant='caption' color='text.secondary'>
                  angelegt {formatDateTime(detailTodo.meta?.createdAt)}
                </Typography>
              </Box>

              {detailTodo.description && (
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                  {detailTodo.description}
                </Typography>
              )}

              {detailTodo.sourceDevFeedbackId?.text && (
                <Paper variant='outlined' sx={{ p: 1.25, borderRadius: 2 }}>
                  <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 600 }}>
                    Rückmeldung
                  </Typography>
                  <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                    {detailTodo.sourceDevFeedbackId.text}
                  </Typography>
                  {detailTodo.sourceDevFeedbackId.user?.username && (
                    <Typography variant='caption' color='text.secondary'>
                      von {detailTodo.sourceDevFeedbackId.user.username} · wird bei
                      Statusänderungen im Verlauf und per Mail informiert
                    </Typography>
                  )}
                </Paper>
              )}

              {canManage && (
                <FormControl size='small' sx={{ maxWidth: 220 }}>
                  <InputLabel id='dev-board-move-label'>Spalte</InputLabel>
                  <Select
                    labelId='dev-board-move-label'
                    label='Spalte'
                    value={columnOf(detailTodo)}
                    disabled={movePending}
                    onChange={event => moveTo(detailTodo, event.target.value)}
                  >
                    {COLUMNS.map(column => (
                      <MenuItem key={column.key} value={column.key}>
                        {column.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {canManage && detailTodo.status !== 'completed' && (
                <Paper variant='outlined' sx={{ p: 1.25, borderRadius: 2 }}>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    spacing={1}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size='small'
                          checked={!!detailTodo.autoRun?.enabled}
                          disabled={autoPending}
                          onChange={event => toggleAutoRun(detailTodo, event.target.checked)}
                        />
                      }
                      label='Automatisch abarbeiten'
                    />
                    {autopilotStatus(detailTodo.autoRun) && (
                      <Typography variant='caption' color='text.secondary'>
                        {autopilotStatus(detailTodo.autoRun).label}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                    {AUTOPILOT_HINT}
                  </Typography>
                  <TextField
                    key={`ai-instructions-${detailTodo._id}`}
                    inputRef={aiInstructionsRef}
                    label='Hinweise für die KI'
                    placeholder='z. B. welcher Standort gemeint ist, welche Lesart gilt, was nicht angefasst werden soll'
                    multiline
                    minRows={3}
                    fullWidth
                    size='small'
                    margin='dense'
                    disabled={aiInstructionsPending}
                    defaultValue={detailTodo.aiInstructions || ''}
                    onBlur={() => saveAiInstructions(detailTodo)}
                    helperText='Geht ausschließlich an den Lauf, der diese Karte übernimmt — nicht an den Melder und nicht in die Beschreibung. Wird beim Verlassen des Feldes gespeichert.'
                  />
                </Paper>
              )}

              {/* Read-only view of the briefing where the switch above is not
                  rendered: a finished card, or a viewer who may not manage. */}
              {!(canManage && detailTodo.status !== 'completed') && detailTodo.aiInstructions && (
                <Paper variant='outlined' sx={{ p: 1.25, borderRadius: 2 }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                  >
                    Hinweise für die KI
                  </Typography>
                  <Typography variant='body2' sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {detailTodo.aiInstructions}
                  </Typography>
                </Paper>
              )}

              {/* The run's report — also for a read-only viewer and for a card
                  that is already done, where the switch above is not rendered.
                  A run writes several sentences here; that belongs on the page,
                  not in hover text. */}
              {autopilotStatus(detailTodo.autoRun) && (
                <Paper variant='outlined' sx={{ p: 1.25, borderRadius: 2, borderStyle: 'dashed' }}>
                  <Stack
                    direction='row'
                    spacing={0.5}
                    sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                    useFlexGap
                  >
                    <RocketLaunchOutlinedIcon sx={{ fontSize: 15, color: 'info.main' }} />
                    <Typography variant='caption' sx={{ fontWeight: 600 }}>
                      {autopilotStatus(detailTodo.autoRun).label}
                    </Typography>
                    {detailTodo.autoRun?.finishedAt && (
                      <Typography variant='caption' color='text.secondary'>
                        · {formatDateTime(detailTodo.autoRun.finishedAt)}
                      </Typography>
                    )}
                  </Stack>
                  {autopilotMarkedBy(detailTodo.autoRun) && (
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                      {autopilotMarkedBy(detailTodo.autoRun)}
                    </Typography>
                  )}
                  {detailTodo.autoRun?.note && (
                    <Typography
                      variant='caption'
                      sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}
                    >
                      {detailTodo.autoRun.note}
                    </Typography>
                  )}
                  <Stack direction='row' spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }} useFlexGap>
                    {(detailTodo.autoRun?.prUrls || []).map((url, index) => (
                      <Chip
                        key={url}
                        component='a'
                        href={url}
                        target='_blank'
                        rel='noreferrer'
                        clickable
                        size='small'
                        variant='outlined'
                        label={`Pull Request${
                          (detailTodo.autoRun.prUrls || []).length > 1 ? ` ${index + 1}` : ''
                        }`}
                      />
                    ))}
                    {detailTodo.autoRun?.sessionUrl && (
                      <Chip
                        component='a'
                        href={detailTodo.autoRun.sessionUrl}
                        target='_blank'
                        rel='noreferrer'
                        clickable
                        size='small'
                        variant='outlined'
                        label='Lauf ansehen'
                      />
                    )}
                  </Stack>
                </Paper>
              )}

              {(detailTodo.devBoard?.history || []).length > 0 && (
                <Box>
                  <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 600 }}>
                    Verlauf
                  </Typography>
                  <Stack spacing={0.25}>
                    {detailTodo.devBoard.history.map((entry, index) => (
                      <Typography key={index} variant='caption' color='text.secondary'>
                        {COLUMN_BY_KEY[entry.from]?.label || RETIRED_COLUMN_LABELS[entry.from] || 'Neu'} →{' '}
                        {COLUMN_BY_KEY[entry.to]?.label || RETIRED_COLUMN_LABELS[entry.to] || entry.to}
                        {entry.username ? ` · ${entry.username}` : ''} · {formatDateTime(entry.at)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
}
